import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import subscriptionsService, {
  SubscriptionPlan,
  Subscription,
  Payment,
  PaymentStats,
  DashboardStats,
  PaginatedResponse,
  SubscriptionPlanCreate,
  SubscriptionPlanUpdate,
  SubscriptionInvitation,
  SubscriptionInvitationCreate,
} from './subscriptionsService';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface SubscriptionsState {
  plans: SubscriptionPlan[];
  currentSubscription: Subscription | null;
  payments: Payment[];
  paymentsPagination: PaginatedResponse<Payment>['pagination'] | null;

  // Admin
  adminPlans: SubscriptionPlan[];
  adminSubscriptions: Subscription[];
  adminSubscriptionsPagination: PaginatedResponse<Subscription>['pagination'] | null;
  adminPayments: Payment[];
  adminPaymentsPagination: PaginatedResponse<Payment>['pagination'] | null;
  adminStats: DashboardStats | null;
  adminPaymentStats: PaymentStats | null;
  adminInvitations: SubscriptionInvitation[];
  adminInvitationsPagination: PaginatedResponse<SubscriptionInvitation>['pagination'] | null;

  isLoading: boolean;
  error: string | null;
}

const initialState: SubscriptionsState = {
  plans: [],
  currentSubscription: null,
  payments: [],
  paymentsPagination: null,
  adminPlans: [],
  adminSubscriptions: [],
  adminSubscriptionsPagination: null,
  adminPayments: [],
  adminPaymentsPagination: null,
  adminStats: null,
  adminPaymentStats: null,
  adminInvitations: [],
  adminInvitationsPagination: null,
  isLoading: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractError(error: unknown): string {
  const axiosError = error as AxiosError<{ detail?: string }>;
  return axiosError.response?.data?.detail || axiosError.message || 'An error occurred';
}

// ---------------------------------------------------------------------------
// Thunks – Owner
// ---------------------------------------------------------------------------

export const fetchPlans = createAsyncThunk('subscriptions/fetchPlans', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsService.getPlans();
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

export const fetchCurrentSubscription = createAsyncThunk(
  'subscriptions/fetchCurrentSubscription',
  async (_, { rejectWithValue }) => {
    try {
      return await subscriptionsService.getCurrentSubscription();
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const createCheckoutSession = createAsyncThunk(
  'subscriptions/createCheckoutSession',
  async (planId: number, { rejectWithValue }) => {
    try {
      return await subscriptionsService.createCheckout(planId);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const createPortalSession = createAsyncThunk(
  'subscriptions/createPortalSession',
  async (_, { rejectWithValue }) => {
    try {
      return await subscriptionsService.createPortalSession();
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const fetchPayments = createAsyncThunk(
  'subscriptions/fetchPayments',
  async (params: { page?: number; page_size?: number } | undefined, { rejectWithValue }) => {
    try {
      return await subscriptionsService.getPayments(params);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const verifyCheckoutSession = createAsyncThunk(
  'subscriptions/verifyCheckoutSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      return await subscriptionsService.verifyCheckoutSession(sessionId);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const changePlan = createAsyncThunk(
  'subscriptions/changePlan',
  async (planId: number, { rejectWithValue }) => {
    try {
      return await subscriptionsService.changePlan(planId);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ---------------------------------------------------------------------------
// Thunks – Admin
// ---------------------------------------------------------------------------

export const adminFetchPlans = createAsyncThunk('subscriptions/adminFetchPlans', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsService.adminGetPlans();
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

export const adminCreatePlan = createAsyncThunk(
  'subscriptions/adminCreatePlan',
  async (data: SubscriptionPlanCreate, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminCreatePlan(data);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminUpdatePlan = createAsyncThunk(
  'subscriptions/adminUpdatePlan',
  async ({ id, data }: { id: number; data: SubscriptionPlanUpdate }, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminUpdatePlan(id, data);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminDeletePlan = createAsyncThunk(
  'subscriptions/adminDeletePlan',
  async (id: number, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminDeletePlan(id);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminFetchSubscriptions = createAsyncThunk(
  'subscriptions/adminFetchSubscriptions',
  async (
    params: { page?: number; page_size?: number; status?: string; company_id?: number; plan_id?: number } | undefined,
    { rejectWithValue },
  ) => {
    try {
      return await subscriptionsService.adminGetSubscriptions(params);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminCancelSubscription = createAsyncThunk(
  'subscriptions/adminCancelSubscription',
  async ({ id, atPeriodEnd }: { id: number; atPeriodEnd?: boolean }, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminCancelSubscription(id, atPeriodEnd);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminFetchPayments = createAsyncThunk(
  'subscriptions/adminFetchPayments',
  async (
    params: { page?: number; page_size?: number; status?: string; company_id?: number } | undefined,
    { rejectWithValue },
  ) => {
    try {
      return await subscriptionsService.adminGetPayments(params);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminFetchStats = createAsyncThunk('subscriptions/adminFetchStats', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsService.adminGetStats();
  } catch (error) {
    return rejectWithValue(extractError(error));
  }
});

export const adminRefundPayment = createAsyncThunk(
  'subscriptions/adminRefundPayment',
  async ({ paymentId, data }: { paymentId: number; data?: { reason?: string; amount?: number } }, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminRefundPayment(paymentId, data);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminFetchPaymentStats = createAsyncThunk(
  'subscriptions/adminFetchPaymentStats',
  async (_, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminGetPaymentStats();
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminSendInvoiceEmail = createAsyncThunk(
  'subscriptions/adminSendInvoiceEmail',
  async (paymentId: number, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminSendInvoiceEmail(paymentId);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminCreateInvitation = createAsyncThunk(
  'subscriptions/adminCreateInvitation',
  async (data: SubscriptionInvitationCreate, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminCreateInvitation(data);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const adminFetchInvitations = createAsyncThunk(
  'subscriptions/adminFetchInvitations',
  async (params: { page?: number; page_size?: number; status?: string } | undefined, { rejectWithValue }) => {
    try {
      return await subscriptionsService.adminGetInvitations(params);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const acceptInvitation = createAsyncThunk(
  'subscriptions/acceptInvitation',
  async (token: string, { rejectWithValue }) => {
    try {
      return await subscriptionsService.acceptInvitation(token);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentSubscription: (state) => {
      state.currentSubscription = null;
    },
  },
  extraReducers: (builder) => {
    // fetchPlans
    builder
      .addCase(fetchPlans.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchPlans.fulfilled, (state, action) => { state.isLoading = false; state.plans = action.payload; })
      .addCase(fetchPlans.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // fetchCurrentSubscription
    builder
      .addCase(fetchCurrentSubscription.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => { state.isLoading = false; state.currentSubscription = action.payload; })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => { state.isLoading = false; state.currentSubscription = null; state.error = action.payload as string; });

    // verifyCheckoutSession
    builder
      .addCase(verifyCheckoutSession.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(verifyCheckoutSession.fulfilled, (state, action) => { state.isLoading = false; state.currentSubscription = action.payload; })
      .addCase(verifyCheckoutSession.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // changePlan
    builder
      .addCase(changePlan.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(changePlan.fulfilled, (state, action) => { state.isLoading = false; state.currentSubscription = action.payload; })
      .addCase(changePlan.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // fetchPayments
    builder
      .addCase(fetchPayments.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payments = action.payload.data;
        state.paymentsPagination = action.payload.pagination;
      })
      .addCase(fetchPayments.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // adminFetchPlans
    builder
      .addCase(adminFetchPlans.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(adminFetchPlans.fulfilled, (state, action) => { state.isLoading = false; state.adminPlans = action.payload; })
      .addCase(adminFetchPlans.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // adminCreatePlan
    builder
      .addCase(adminCreatePlan.fulfilled, (state, action) => { state.adminPlans.push(action.payload); });

    // adminUpdatePlan
    builder
      .addCase(adminUpdatePlan.fulfilled, (state, action) => {
        const idx = state.adminPlans.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.adminPlans[idx] = action.payload;
      });

    // adminDeletePlan (deactivate)
    builder
      .addCase(adminDeletePlan.fulfilled, (state, action) => {
        const idx = state.adminPlans.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.adminPlans[idx] = action.payload;
      });

    // adminFetchSubscriptions
    builder
      .addCase(adminFetchSubscriptions.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(adminFetchSubscriptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminSubscriptions = action.payload.data;
        state.adminSubscriptionsPagination = action.payload.pagination;
      })
      .addCase(adminFetchSubscriptions.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // adminCancelSubscription
    builder
      .addCase(adminCancelSubscription.fulfilled, (state, action) => {
        const idx = state.adminSubscriptions.findIndex((s) => s.id === action.payload.id);
        if (idx !== -1) state.adminSubscriptions[idx] = action.payload;
      });

    // adminFetchPayments
    builder
      .addCase(adminFetchPayments.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(adminFetchPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminPayments = action.payload.data;
        state.adminPaymentsPagination = action.payload.pagination;
      })
      .addCase(adminFetchPayments.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // adminFetchStats
    builder
      .addCase(adminFetchStats.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(adminFetchStats.fulfilled, (state, action) => { state.isLoading = false; state.adminStats = action.payload; })
      .addCase(adminFetchStats.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // adminRefundPayment
    builder
      .addCase(adminRefundPayment.fulfilled, (state, action) => {
        const idx = state.adminPayments.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.adminPayments[idx] = action.payload;
      });

    // adminFetchPaymentStats
    builder
      .addCase(adminFetchPaymentStats.fulfilled, (state, action) => { state.adminPaymentStats = action.payload; });

    // adminFetchInvitations
    builder
      .addCase(adminFetchInvitations.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(adminFetchInvitations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminInvitations = action.payload.data;
        state.adminInvitationsPagination = action.payload.pagination;
      })
      .addCase(adminFetchInvitations.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // adminCreateInvitation
    builder
      .addCase(adminCreateInvitation.fulfilled, (state, action) => {
        state.adminInvitations.unshift(action.payload);
      });
  },
});

export const { clearError, clearCurrentSubscription } = subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;
